import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { F, th as defaultPalette } from "@/lib/bricks";
import MapCutout from "@/components/MapCutout";

const PROVINCE_NAMES = {
  ontario: "ON",
  quebec: "QC",
  "québec": "QC",
  "british columbia": "BC",
  alberta: "AB",
  manitoba: "MB",
  saskatchewan: "SK",
  "nova scotia": "NS",
  "new brunswick": "NB",
  "newfoundland and labrador": "NL",
  newfoundland: "NL",
  "prince edward island": "PE",
  yukon: "YT",
  "northwest territories": "NT",
  nunavut: "NU",
};

/** Map a Mapbox region name/short_code to a 2-letter code. Unmapped values return "". */
export function mapProvince(raw) {
  if (!raw) return "";
  const t = String(raw).trim();
  if (/^[A-Za-z]{2}$/.test(t)) return t.toUpperCase();
  const ca = t.match(/^CA-([A-Za-z]{2})$/i);
  if (ca) return ca[1].toUpperCase();
  return PROVINCE_NAMES[t.toLowerCase()] || "";
}

export function readCoords(obj) {
  if (!obj || typeof obj !== "object") return null;
  const lng = Number(obj.lng ?? obj.longitude ?? obj.lon);
  const lat = Number(obj.lat ?? obj.latitude);
  if (Number.isFinite(lng) && Number.isFinite(lat)) return { lng, lat };
  return null;
}

function parseFeature(feat) {
  const ctx = feat.context || [];
  const find = (prefix) => ctx.find((c) => String(c.id || "").startsWith(prefix));
  const place = find("place.") || find("locality.");
  const region = find("region.");
  const postcode = find("postcode.");
  let province = "";
  if (region) {
    const short = String(region.short_code || "").toUpperCase();
    province = short.startsWith("CA-") && short.length === 5 ? short.slice(3) : (region.text || "");
  }
  const center = feat.center || [];
  return {
    label: feat.place_name || feat.text || "",
    city: (place && place.text) || "",
    province,
    postal_code: (postcode && postcode.text) || "",
    lng: center[0],
    lat: center[1],
  };
}

function suggest(q) {
  const pk = process.env.REACT_APP_MAPBOX_TOKEN || "";
  if (pk) {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${encodeURIComponent(pk)}&country=ca&types=address&limit=6`;
    return fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("mapbox"))))
      .then((d) => (d.features || []).map(parseFeature));
  }
  return api.get("/address-suggest", { params: { q } }).then((r) => (Array.isArray(r.data) ? r.data : []));
}

/** Standard 3D cutout for a saved deal/listing address. Uses stored lng/lat when present, otherwise geocodes once. */
export function GeocodedMapCutout({ address, city, province, lng, lat, dark, label }) {
  const token = process.env.REACT_APP_MAPBOX_TOKEN || "";
  const [pin, setPin] = useState(() => readCoords({ lng, lat }));

  useEffect(() => {
    const stored = readCoords({ lng, lat });
    if (stored) {
      setPin(stored);
      return undefined;
    }
    const q = [address, city, province].filter(Boolean).join(", ").trim();
    if (!token || q.length < 5) {
      setPin(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      suggest(q)
        .then((list) => {
          if (cancelled) return;
          const first = list[0];
          const next = readCoords(first);
          setPin(next);
        })
        .catch(() => { if (!cancelled) setPin(null); });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [address, city, province, lng, lat, token]);

  if (!token || !pin) return null;
  return (
    <MapCutout
      token={token}
      lng={pin.lng}
      lat={pin.lat}
      label={label || address}
      dark={dark}
    />
  );
}

export default function AddressAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder,
  style,
  className,
  p: pProp,
  dark,
  autoFocus,
  lng: lngProp,
  lat: latProp,
}) {
  const p = pProp || defaultPalette(!!dark);
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [hover, setHover] = useState(-1);
  const [pin, setPin] = useState(() => readCoords({ lng: lngProp, lat: latProp }));
  const [typed, setTyped] = useState(false);

  useEffect(() => {
    if (!(value || "").trim()) setPin(null);
  }, [value]);

  // Seed pin from stored coords, or geocode a prefilled address once (no dropdown).
  useEffect(() => {
    const stored = readCoords({ lng: lngProp, lat: latProp });
    if (stored) {
      setPin(stored);
      return undefined;
    }
    if (typed) return undefined;
    const q = (value || "").trim();
    if (q.length < 5) return undefined;
    let cancelled = false;
    const t = setTimeout(() => {
      suggest(q)
        .then((list) => {
          if (cancelled) return;
          const next = readCoords(list[0]);
          if (next) setPin((prev) => prev || next);
        })
        .catch(() => {});
    }, 280);
    return () => { cancelled = true; clearTimeout(t); };
  }, [value, typed, lngProp, latProp]);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 3) {
      setItems([]);
      setOpen(false);
      return undefined;
    }
    if (!typed) return undefined;
    const t = setTimeout(() => {
      suggest(q)
        .then((list) => {
          setItems(list);
          setOpen(list.length > 0);
          setHover(-1);
        })
        .catch(() => {
          setItems([]);
          setOpen(false);
        });
    }, 300);
    return () => clearTimeout(t);
  }, [value, typed]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const pick = (s) => {
    onChange?.(s.label || "");
    if (s.lng != null && s.lat != null && Number.isFinite(Number(s.lng)) && Number.isFinite(Number(s.lat))) {
      setPin({ lng: Number(s.lng), lat: Number(s.lat) });
    } else {
      setPin(null);
    }
    setOpen(false);
    setItems([]);
    onSelect?.(s);
  };

  const publicToken = process.env.REACT_APP_MAPBOX_TOKEN || "";

  return (
    <div ref={wrapRef} className={className} style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        value={value}
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => {
          setTyped(true);
          onChange?.(e.target.value);
          setOpen(true);
        }}
        onFocus={() => { if (items.length) setOpen(true); }}
        onKeyDown={(e) => {
          if (!open || !items.length) {
            if (e.key === "Escape") setOpen(false);
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHover((h) => (h + 1) % items.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHover((h) => (h <= 0 ? items.length - 1 : h - 1));
          } else if (e.key === "Enter" && hover >= 0) {
            e.preventDefault();
            pick(items[hover]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        style={style}
      />
      {open && items.length > 0 && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "100%",
            marginTop: 4,
            background: p.surf || p.bg,
            border: `1px solid ${p.hairS || p.hair}`,
            borderRadius: 2,
            zIndex: 200,
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          {items.map((s, i) => (
            <button
              key={`${s.label}-${i}`}
              type="button"
              role="option"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s)}
              onMouseEnter={() => setHover(i)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "10px 12px",
                background: hover === i ? (p.surf2 || p.hair) : "transparent",
                border: "none",
                borderBottom: i === items.length - 1 ? "none" : `1px solid ${p.hair}`,
                cursor: "pointer",
                fontFamily: F.display,
                fontSize: 13,
                color: p.ink,
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
      {publicToken && pin && Number.isFinite(pin.lng) && Number.isFinite(pin.lat) && (
        <MapCutout
          token={publicToken}
          lng={pin.lng}
          lat={pin.lat}
          label={value}
          dark={dark}
        />
      )}
    </div>
  );
}
